import React, { useState, useRef } from 'react';
import {
    Box,
    Button,
    Dialog,
    DialogTitle,
    DialogContent,
    Typography,
    IconButton
} from '@mui/material';
import { GridColDef, GridRenderCellParams } from '@mui/x-data-grid-pro';
import { Close, Refresh, Cancel, Description } from '@mui/icons-material';
import { useSelector } from 'react-redux';
import { RootState } from '../../../../store/store';
import { enqueueSnackbar } from 'notistack';
import Table from '../../../../components/table/Table';
import {
    useFetchDataQuery,
    useCancelCorporateSendMoneyMutation,
    useRetryCorporateSendMoneyMutation
} from '../../services/services';
import { ConfirmationModal } from '../../../../components/ConfirmationModal';
import { GridInitialStatePro } from '@mui/x-data-grid-pro/models/gridStatePro';
import { useSnackbar } from 'notistack';
import { formatMoney } from '../../../../utils/currencyConvert';

interface DetailData {
    id: number;
    batchId: string;
    receiverName: string;
    receiverCardNo: string;
    statusDescription: string;
    value: number;
    description: string;
    isSend: boolean;
    status: number;
}

interface MealAllowanceDetailModalProps {
    open: boolean;
    onClose: () => void;
    batchId: string;
    status: string;
}

const MealAllowanceDetailModal: React.FC<MealAllowanceDetailModalProps> = ({
    open,
    onClose,
    batchId,
    status
}) => {
    const { enqueueSnackbar } = useSnackbar();
    const [columnVisibilityModel, setColumnVisibilityModel] = useState<Record<string, boolean>>({});
    const [confirmModal, setConfirmModal] = useState<{
        open: boolean;
        title: string;
        onConfirm: () => void;
    }>({
        open: false,
        title: '',
        onConfirm: () => { }
    });

    const [requestBody, setRequestBody] = useState({
        filters: [],
        pageSize: 25,
        page: 1,
        orderBy: "",
        orderDesc: false,
    });

    const [paginationModel, setPaginationModel] = useState({
        page: 0,
        pageSize: 25,
    });

    const listUrl = `/Process/GetSendMealAllowanceListStatusDetailV2?batchId=${batchId}`;

    // API çağrısı
    const { data: fetchData, isLoading: isFetchLoading, refetch } = useFetchDataQuery({
        url: listUrl,
        filter: requestBody,
        method: "POST"
    }, {
        skip: !batchId || !open
    });

    const cardList = fetchData?.data || [];

    const waitingStatusCount = cardList.filter((card: DetailData) => card.status === 0 || card.status === 3).length;

    // RTK Query mutations
    const [cancelMutation, { isLoading: isCancelLoading }] = useCancelCorporateSendMoneyMutation();
    const [retryMutation, { isLoading: isRetryLoading }] = useRetryCorporateSendMoneyMutation();

    // Loading control refs
    const isProcessingRef = useRef(false);

    const canCancel = (isSend: boolean, status: number): boolean => {
        if (isSend === false && (status === null || status === 0)) {
            return true; // Beklemede olan bir işlem iptal edilebilir
        } else if (isSend === false && status === 3) {
            return true; // Başarısız bir işlem iptal edilebilir
        }
        return false;
    };

    const canRetry = (isSend: boolean, status: number): boolean => {
        if (isSend === false && status === 3) {
            return true; // Başarısız bir işlem tekrar edilebilir
        }
        return false;
    };

    const isCancelActive = cardList.some((card: DetailData) => canCancel(card.isSend, card.status));
    const isRetryActive = cardList.some((card: DetailData) => canRetry(card.isSend, card.status));

    // Action handlers
    const handleCancelAction = async (data: DetailData) => {
        if (isProcessingRef.current) return;
        isProcessingRef.current = true;

        try {
            const result = await cancelMutation({
                id: data.id,
                batchId: null
            }).unwrap();

            const message = result?.message || 'İptal işlemi başarıyla tamamlandı';
            enqueueSnackbar(message, { variant: 'success' });
            refetch();
        } catch (error: any) {
            console.error('Cancel error:', error);
            const errorMessage = error?.data?.message || 'İptal işlemi başarısız oldu';
            enqueueSnackbar(errorMessage, { variant: 'error' });
        } finally {
            isProcessingRef.current = false;
        }
    };

    const handleRetryAction = async (data: DetailData) => {
        if (isProcessingRef.current) return;
        isProcessingRef.current = true;

        try {
            const result = await retryMutation({
                id: data.id,
                batchId: null
            }).unwrap();

            const message = result?.message || 'Tekrar gönderme işlemi başarıyla tamamlandı';
            enqueueSnackbar(message, { variant: 'success' });
            refetch();
        } catch (error: any) {
            console.error('Retry error:', error);
            const errorMessage = error?.data?.message || 'Tekrar gönderme işlemi başarısız oldu';
            enqueueSnackbar(errorMessage, { variant: 'error' });
        } finally {
            isProcessingRef.current = false;
        }
    };

    const handleCancelAll = async () => {
        if (isProcessingRef.current) return;
        isProcessingRef.current = true;

        try {
            const result = await cancelMutation({
                id: null,
                batchId: batchId
            }).unwrap();

            const message = result?.message || 'Toplu iptal işlemi başarıyla tamamlandı';
            enqueueSnackbar(message, { variant: 'success' });
            refetch();
        } catch (error: any) {
            console.error('Cancel all error:', error);
            const errorMessage = error?.data?.message || 'Toplu iptal işlemi başarısız oldu';
            enqueueSnackbar(errorMessage, { variant: 'error' });
        } finally {
            isProcessingRef.current = false;
        }
    };

    const handleRetryAll = async () => {
        if (isProcessingRef.current) return;
        isProcessingRef.current = true;

        try {
            const result = await retryMutation({
                id: null,
                batchId: batchId
            }).unwrap();

            const message = result?.message || 'Toplu tekrar gönderme işlemi başarıyla tamamlandı';
            enqueueSnackbar(message, { variant: 'success' });
            refetch();
        } catch (error: any) {
            console.error('Retry all error:', error);
            const errorMessage = error?.data?.message || 'Toplu tekrar gönderme işlemi başarısız oldu';
            enqueueSnackbar(errorMessage, { variant: 'error' });
        } finally {
            isProcessingRef.current = false;
        }
    };

    const showConfirmation = (title: string, onConfirm: () => void) => {
        setConfirmModal({
            open: true,
            title,
            onConfirm
        });
    };

    const columns: GridColDef[] = [
        {
            field: 'receiverName',
            headerName: 'Kullanıcı Adı',
            width: 200,
            flex: 1,
            minWidth: 150,
        },
        {
            field: 'receiverCardNo',
            headerName: 'Kart Numarası',
            width: 180,
            flex: 1,
            minWidth: 130,
        },
        {
            field: 'statusDescription',
            headerName: 'Statü',
            width: 150,
            flex: 0,
            minWidth: 120,
        },
        {
            field: 'value', 
            headerName: 'Yüklenecek Bakiye', 
            width: 180,
            flex: 0,
            minWidth: 140,
            type: 'number',
            renderCell: (params: GridRenderCellParams) => formatMoney(params, 'value'),
        },
        {
            field: 'description',
            headerName: 'Açıklama',
            width: 200,
            flex: 1,
            minWidth: 150,
        },
        {
            field: 'actions',
            headerName: 'Aksiyonlar',
            width: 250,
            flex: 0,
            minWidth: 150,
            align: 'center',
            headerAlign: 'center',
            renderCell: (params: GridRenderCellParams<DetailData>) => {
                const row = params.row;
                return (
                    <Box sx={{ 
                        display: 'flex', 
                        flexDirection: 'row',
                        gap: { xs: 0.5, sm: 1 }, 
                        justifyContent: 'center', 
                        alignItems: 'center',
                        flexWrap: 'nowrap',
                        height: '100%',
                        width: '100%',
                        boxSizing: 'border-box',
                    }}>
                        {canCancel(row.isSend, row.status) && (
                            <Button
                                variant="contained"
                                size="small"
                                startIcon={<Cancel sx={{ fontSize: { xs: 12, sm: 16 } }} />}
                                disabled={isCancelLoading || isRetryLoading}
                                onClick={() => showConfirmation(
                                    'İlgili satır iptal edilecektir. Onaylıyor musunuz?',
                                    () => handleCancelAction(row)
                                )}
                                sx={{
                                    bgcolor: '#f44336',
                                    minWidth: { xs: '60px', sm: '70px' },
                                    maxWidth: { xs: '60px', sm: '70px' },
                                    width: { xs: '60px', sm: '70px' },
                                    fontSize: { xs: '0.7rem', sm: '0.875rem' },
                                    py: 0.5,
                                    height: { xs: '24px', sm: '26.84px' },
                                    boxSizing: 'border-box',
                                    padding: '0 !important',
                                    whiteSpace: 'nowrap',
                                    '&:hover': { bgcolor: '#da190b' },
                                }}
                            >
                                İptal
                            </Button>
                        )}
                        {canRetry(row.isSend, row.status) && (
                            <Button
                                variant="contained"
                                size="small"
                                startIcon={<Refresh sx={{ fontSize: { xs: 12, sm: 16 } }} />}
                                disabled={isCancelLoading || isRetryLoading}
                                onClick={() => showConfirmation(
                                    'İlgili satır tekrar denenmek üzere işleme alınacaktır. Onaylıyor musunuz?',
                                    () => handleRetryAction(row)
                                )}
                                sx={{
                                    bgcolor: '#4caf50',
                                    minWidth: { xs: '120px', sm: '150px' },
                                    width: { xs: '120px', sm: '150px' },
                                    fontSize: { xs: '0.7rem', sm: '0.875rem' },
                                    p: { xs: '6px 8px !important', sm: '10px !important' },
                                    height: { xs: '24px', sm: '26.84px' },
                                    boxSizing: 'border-box',
                                    margin: '0 !important',
                                    fontWeight: 600,
                                    whiteSpace: 'nowrap',
                                    '&:hover': { bgcolor: '#45a049' },
                                }}
                            >
                                Tekrar Gönder
                            </Button>
                        )}
                    </Box>
                );
            },
            sortable: false,
            filterable: false,
        },
    ];

    const debounce = (func: any, delay: any) => {
        let timeoutId: any;
        return (...args: any) => {
            clearTimeout(timeoutId);
            timeoutId = setTimeout(() => {
                func.apply(null, args);
            }, delay);
        };
    };

    const onFilterChange = debounce((filterObj: any) => {
        setRequestBody((prev) => ({ ...prev, filters: filterObj }));
    }, 500);

    const handlePaginationModelChange = (model: any) => {
        setPaginationModel(model);
        setRequestBody((prev) => ({ ...prev, pageSize: model.pageSize, page: model.page + 1 }));
    };

    const handleSortModelChange = (model: any) => {
        setRequestBody((prev) => ({ ...prev, orderBy: model[0]?.field ?? "", orderDesc: model[0]?.sort === "asc" }));
    };

    return (
        <>
            <Dialog
                open={open}
                onClose={onClose}
                maxWidth="xl"
                fullWidth
                PaperProps={{
                    sx: (theme) => ({
                        width: { xs: '95vw', sm: '90vw', md: '90vw' },
                        height: { xs: '90vh', sm: '85vh', md: '80vh' },
                        maxWidth: 'none',
                        m: { xs: 1, sm: 2, md: 3 },
                        backgroundColor: theme.palette.grey[50],
                        overflow: 'hidden',
                    }),
                }}
            >
                <DialogTitle component="div" sx={{ p: 0, m: 0 }}>
                    <Box
                        sx={{
                            background: 'linear-gradient(135deg, #1565c0 0%, #0d47a1 100%)',
                            padding: { xs: '12px 16px', sm: '16px 20px' },
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            gap: 2,
                            position: 'relative',
                            overflow: 'hidden',
                            '&::before': {
                                content: '""',
                                position: 'absolute',
                                top: 0,
                                left: 0,
                                right: 0,
                                bottom: 0,
                                background: 'rgba(255,255,255,0.1)',
                                backdropFilter: 'blur(10px)',
                                zIndex: 0,
                            },
                        }}
                    >
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, position: 'relative', zIndex: 1, minWidth: 0 }}>
                            <Box
                                sx={{
                                    width: 32,
                                    height: 32,
                                    borderRadius: 1,
                                    bgcolor: 'rgba(255,255,255,0.2)',
                                    color: 'white',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    flexShrink: 0,
                                }}
                            >
                                <Description sx={{ fontSize: 18 }} />
                            </Box>
                            <Typography
                                variant="h6"
                                sx={{
                                    color: 'white',
                                    fontWeight: 700,
                                    fontSize: { xs: '0.95rem !important', sm: '1rem !important' },
                                    textShadow: '0 1px 3px rgba(0,0,0,0.3)',
                                    lineHeight: 1.3,
                                }}
                            >
                                Bakiye Yükleme Detayları
                            </Typography>
                        </Box>
                        <IconButton
                            onClick={onClose}
                            sx={{
                                color: 'white',
                                position: 'relative',
                                zIndex: 1,
                                flexShrink: 0,
                                width: 36,
                                height: 36,
                                border: '1px solid',
                                borderColor: 'rgba(255,255,255,0.3)',
                                backgroundColor: 'rgba(255,255,255,0.1)',
                                '&:hover': {
                                    backgroundColor: 'rgba(255,255,255,0.2)',
                                    borderColor: 'rgba(255,255,255,0.5)',
                                    transform: 'rotate(90deg)',
                                },
                                transition: 'all 0.2s ease-in-out',
                            }}
                        >
                            <Close sx={{ fontSize: 20 }} />
                        </IconButton>
                    </Box>
                    {(isCancelActive || isRetryActive) && (
                        <Box
                            sx={{
                                px: { xs: 2, sm: 3 },
                                py: { xs: 1.5, sm: 2 },
                                borderBottom: '1px solid',
                                borderColor: 'divider',
                                bgcolor: 'background.paper',
                                display: 'flex',
                                flexDirection: { xs: 'column', sm: 'row' },
                                gap: { xs: 1, sm: 1.5 },
                                alignItems: 'stretch',
                                justifyContent: 'flex-end',
                            }}
                        >
                                {isCancelActive && (
                                    <Button
                                        variant="contained"
                                        size="medium"
                                        startIcon={<Cancel sx={{ fontSize: { xs: 16, sm: 18 } }} />}
                                        disabled={isCancelLoading || isRetryLoading}
                                        onClick={() => showConfirmation(
                                            `${waitingStatusCount} talimat iptal edilecektir. Onaylıyor musunuz?`,
                                            handleCancelAll
                                        )}
                                        sx={{
                                            bgcolor: '#f44336',
                                            minWidth: { xs: '100%', sm: '130px' },
                                            fontSize: { xs: '0.8125rem', sm: '0.875rem' },
                                            py: { xs: 0.75, sm: 1 },
                                            px: { xs: 1.5, sm: 2 },
                                            height: { xs: 32, sm: 36 },
                                            textTransform: 'none',
                                            fontWeight: 500,
                                            borderRadius: 1,
                                            boxShadow: '0 2px 4px rgba(244, 67, 54, 0.2)',
                                            '& .MuiButton-startIcon': { marginRight: { xs: 0.5, sm: 0.75 } },
                                            '&:hover': { 
                                                bgcolor: '#d32f2f',
                                                boxShadow: '0 4px 8px rgba(244, 67, 54, 0.3)',
                                                transform: 'translateY(-1px)'
                                            },
                                            '&:disabled': {
                                                bgcolor: 'rgba(244, 67, 54, 0.5)',
                                            },
                                            transition: 'all 0.2s ease-in-out'
                                        }}
                                    >
                                        Tümünü İptal Et
                                    </Button>
                                )}
                                {isRetryActive && (
                                    <Button
                                        variant="contained"
                                        size="medium"
                                        startIcon={<Refresh sx={{ fontSize: { xs: 16, sm: 18 } }} />}
                                        disabled={isCancelLoading || isRetryLoading}
                                        onClick={() => showConfirmation(
                                            `${waitingStatusCount} talimat tekrar denenmek üzere işleme alınacaktır. Onaylıyor musunuz?`,
                                            handleRetryAll
                                        )}
                                        sx={{
                                            bgcolor: '#4caf50',
                                            minWidth: { xs: '100%', sm: '160px' },
                                            fontSize: { xs: '0.8125rem', sm: '0.875rem' },
                                            py: { xs: 0.75, sm: 1 },
                                            px: { xs: 1.5, sm: 2 },
                                            height: { xs: 32, sm: 36 },
                                            textTransform: 'none',
                                            fontWeight: 500,
                                            borderRadius: 1,
                                            boxShadow: '0 2px 4px rgba(76, 175, 80, 0.2)',
                                            '& .MuiButton-startIcon': { marginRight: { xs: 0.5, sm: 0.75 } },
                                            '&:hover': { 
                                                bgcolor: '#388e3c',
                                                boxShadow: '0 4px 8px rgba(76, 175, 80, 0.3)',
                                                transform: 'translateY(-1px)'
                                            },
                                            '&:disabled': {
                                                bgcolor: 'rgba(76, 175, 80, 0.5)',
                                            },
                                            transition: 'all 0.2s ease-in-out'
                                        }}
                                    >
                                        Tümünü Tekrar Gönder
                                    </Button>
                                )}
                        </Box>
                    )}
                </DialogTitle>

                <DialogContent
                    sx={{
                        px: { xs: 2, sm: 3 },
                        py: 0,
                        pb: { xs: 2, sm: 3 },
                        overflow: 'hidden',
                    }}
                >
                    <Box sx={{ pt: { xs: 2.5, sm: 3 }, width: '100%' }}>
                    <Box
                        sx={{
                            height: { xs: 'calc(90vh - 200px)', sm: 'calc(85vh - 180px)', md: '60vh' },
                            width: '100%',
                        }}
                    >
                        <Table
                            rows={cardList}
                            columns={columns}
                            getRowId={(row) => row.id}
                            paginationModel={paginationModel}
                            onPaginationModelChange={handlePaginationModelChange}
                            sortModelChange={handleSortModelChange}
                            headerFilters={true}
                            columnVisibilityConfigKey="mealAllowanceDetail"
                            filterKey="mealAllowanceDetailFilter"
                            handleFilterChange={onFilterChange}
                            setColumnVisibilityModel={setColumnVisibilityModel}
                            columnVisibilityModel={columnVisibilityModel}
                            idName="id"
                            listUrl={listUrl}
                            fetchData={undefined}
                            loading={isFetchLoading}
                            rowCount={fetchData?.totalCount || 0}
                            requestBody={requestBody}
                            fetchMethod="POST"
                            initialState={{
                                pinnedColumns: {
                                    right: ['actions'],
                                },
                            } as GridInitialStatePro}
                            sx={{
                                margin: fetchData?.data?.length > 0 && paginationModel.pageSize > 25 ? "0px 0 25px 0 !important" : "0px !important",
                                '& .MuiDataGrid-root': {
                                    borderRadius: 2,
                                    border: 'none',
                                },
                                '& .MuiDataGrid-main': {
                                    borderRadius: 2,
                                },
                                '& .MuiDataGrid-columnHeaders': {
                                    background: 'linear-gradient(135deg, rgba(248, 250, 252, 0.9) 0%, rgba(241, 245, 249, 0.95) 100%)',
                                    borderBottom: '2px solid rgba(226, 232, 240, 0.8)',
                                    borderRadius: '8px 8px 0 0',
                                },
                                '& .MuiDataGrid-cell': {
                                    borderBottom: '1px solid rgba(226, 232, 240, 0.5)',
                                    fontSize: { xs: '0.75rem', sm: '0.875rem' },
                                    padding: { xs: '8px 4px', sm: '8px' },
                                },
                                '& .MuiDataGrid-cell[data-field="actions"]': {
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                },
                                '& .MuiDataGrid-columnHeader[data-field="actions"] .MuiDataGrid-columnHeaderTitleContainer': {
                                    justifyContent: 'center',
                                },
                                '& .MuiDataGrid-columnHeader': {
                                    fontSize: { xs: '0.75rem', sm: '0.875rem' },
                                    padding: { xs: '8px 4px', sm: '8px' },
                                },
                                '& .MuiDataGrid-row': {
                                    '&:hover': {
                                        background: 'rgba(59, 130, 246, 0.04)',
                                    },
                                    '&.Mui-selected': {
                                        background: 'rgba(30, 60, 114, 0.08)',
                                        '&:hover': {
                                            background: 'rgba(30, 60, 114, 0.12)',
                                        },
                                    },
                                },
                            }}
                        />
                    </Box>
                    </Box>
                </DialogContent>
            </Dialog>

            <ConfirmationModal
                isOpen={confirmModal.open}
                onClose={() => setConfirmModal(prev => ({ ...prev, open: false }))}
                onConfirm={() => {
                    confirmModal.onConfirm();
                    setConfirmModal(prev => ({ ...prev, open: false }));
                }}
                title="İşlem Onayı"
                content={confirmModal.title}
                confirmButtonText="Evet"
                closeButtonText="Hayır"
                isLoading={false}
                optionalWidth="auto"
            />
        </>
    );
};

export default MealAllowanceDetailModal;
